import { create } from 'zustand'
import type {
  Workflow,
  WorkflowVersion,
  WorkflowDiff,
} from '@/schemas/workflow'
import type { WorkflowExport } from '@/schemas/workflow-export'
import { workflowLifecycleService } from '@/services/workflow-lifecycle'

interface WorkflowLifecycleState {
  // Versions
  versions: WorkflowVersion[]
  versionsLoading: boolean
  versionsError: string | null
  versionsTotal: number

  // Diff cache keyed by `${workflowId}:${from}->${to}`
  diffCache: Record<string, WorkflowDiff>
  diffLoading: boolean
  diffError: string | null

  // Import
  importing: boolean
  importError: string | null

  // Session-only dismissal state for the "edit published = new draft" banner.
  // Keyed by workflowId — not persisted to localStorage per UI-SPEC.
  editPublishedBannerDismissed: Record<string, boolean>

  // Actions
  fetchVersions: (
    projectId: string,
    workflowId: string,
    page?: number
  ) => Promise<void>
  publish: (
    projectId: string,
    workflowId: string,
    note?: string | null
  ) => Promise<WorkflowVersion | { error: 'nothing_to_publish' } | null>
  unpublish: (projectId: string, workflowId: string) => Promise<boolean>
  restoreVersion: (
    projectId: string,
    workflowId: string,
    versionNumber: number
  ) => Promise<boolean>
  fetchDiff: (
    projectId: string,
    workflowId: string,
    fromVersion: number,
    toVersion: number
  ) => Promise<WorkflowDiff | null>
  importWorkflow: (
    projectId: string,
    payload: WorkflowExport
  ) => Promise<{ workflow: Workflow; needs_attention_nodes: string[] } | null>
  dismissEditPublishedBanner: (workflowId: string) => void
  reset: () => void
}

const initialState = {
  versions: [] as WorkflowVersion[],
  versionsLoading: false,
  versionsError: null as string | null,
  versionsTotal: 0,
  diffCache: {} as Record<string, WorkflowDiff>,
  diffLoading: false,
  diffError: null as string | null,
  importing: false,
  importError: null as string | null,
  editPublishedBannerDismissed: {} as Record<string, boolean>,
}

export const useWorkflowLifecycleStore = create<WorkflowLifecycleState>(
  (set, get) => ({
    ...initialState,

    fetchVersions: async (projectId, workflowId, page = 1) => {
      set({ versionsLoading: true, versionsError: null })
      try {
        const data = await workflowLifecycleService.listVersions(
          projectId,
          workflowId,
          { page, page_size: 20 }
        )
        set({
          versions: data.versions,
          versionsTotal: data.total,
          versionsLoading: false,
        })
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Falha ao carregar versões'
        set({ versionsError: message, versionsLoading: false })
      }
    },

    publish: async (projectId, workflowId, note) => {
      try {
        const v = await workflowLifecycleService.publish(
          projectId,
          workflowId,
          { note: note ?? null }
        )
        // refresh versions after successful publish
        await get().fetchVersions(projectId, workflowId)
        return v
      } catch (e: unknown) {
        // Branch on 409 "Nada a publicar" — backend signals no-change publish
        // via HTTP 409 + detail string. Callers render a distinct toast.
        const err = e as { response?: { status?: number; data?: { detail?: string } } }
        if (
          err?.response?.status === 409 &&
          err?.response?.data?.detail === 'Nada a publicar'
        ) {
          return { error: 'nothing_to_publish' as const }
        }
        const message = e instanceof Error ? e.message : 'Falha ao publicar'
        set({ versionsError: message })
        return null
      }
    },

    unpublish: async (projectId, workflowId) => {
      try {
        await workflowLifecycleService.unpublish(projectId, workflowId)
        return true
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Falha ao despublicar'
        set({ versionsError: message })
        return false
      }
    },

    restoreVersion: async (projectId, workflowId, versionNumber) => {
      try {
        await workflowLifecycleService.restore(
          projectId,
          workflowId,
          versionNumber
        )
        return true
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Falha ao restaurar'
        set({ versionsError: message })
        return false
      }
    },

    fetchDiff: async (projectId, workflowId, fromVersion, toVersion) => {
      const key = `${workflowId}:${fromVersion}->${toVersion}`
      const cached = get().diffCache[key]
      if (cached) return cached
      set({ diffLoading: true, diffError: null })
      try {
        const d = await workflowLifecycleService.diff(
          projectId,
          workflowId,
          fromVersion,
          toVersion
        )
        set((s) => ({
          diffCache: { ...s.diffCache, [key]: d },
          diffLoading: false,
        }))
        return d
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Falha ao carregar diff'
        set({ diffError: message, diffLoading: false })
        return null
      }
    },

    importWorkflow: async (projectId, payload) => {
      set({ importing: true, importError: null })
      try {
        const res = await workflowLifecycleService.importWorkflow(
          projectId,
          payload
        )
        // Decorate imported workflow nodes with `needs_attention` transient
        // flag (not persisted to DB) so custom nodes can render the amber
        // badge on first load. The flag lives on the node itself; no extra
        // store state required. Cleared by the user editing + saving the
        // node (handled in the node-update path).
        const attentionSet = new Set(res.needs_attention_nodes)
        const decoratedNodes = res.workflow.nodes.map((n) =>
          attentionSet.has(n.id)
            ? ({ ...n, needs_attention: true } as typeof n & {
                needs_attention: boolean
              })
            : n
        )
        const decorated = {
          ...res,
          workflow: { ...res.workflow, nodes: decoratedNodes },
        }
        set({ importing: false })
        return decorated
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Falha ao importar'
        set({ importError: message, importing: false })
        return null
      }
    },

    dismissEditPublishedBanner: (workflowId) =>
      set((s) => ({
        editPublishedBannerDismissed: {
          ...s.editPublishedBannerDismissed,
          [workflowId]: true,
        },
      })),

    reset: () => set(initialState),
  })
)
